import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { MouseEventParams, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { scaledCoordinate } from '../drawing.helper'
import { ChartEvents } from '../../chart.events'
import { CorporateAction, Type } from '../../../../proto/generated/corporate-action'
import { getTimeIndex } from '../../../../util/chart'

export type CorporateActionType = 'corporateAction'

export type CorporateActionState = {
    data: Record<number, CorporateAction[]>
}


export type CorporateActionOptions = {
    drawingType: CorporateActionType
}

export type CorporateActionDrawingSettings = {
    data: Record<number, CorporateAction[]>
}

const getColorForAction = (type: Type): string => {
    switch (type) {
        case Type.BONUS:
            return '#B8860B'
        case Type.SPECIAL_DIVIDEND:
            return '#0f89e5'
        case Type.FINAL_DIVIDEND:
            return '#0f89e5'
        case Type.RIGHTS:
            return 'rgb(171, 71, 188)'
        case Type.INTERIM_DIVIDEND:
            return '#0f89e5'
        case Type.SPLIT:
            return 'rgb(251, 140, 0)'
    }
    return '#ffffff'
}

const getCorporateActionLabel = (type: Type): string => {
    switch (type) {
        case Type.BONUS:
            return 'B'
        case Type.SPLIT:
            return 'S'
        case Type.RIGHTS:
            return 'R'
        case Type.INTERIM_DIVIDEND:
            return 'D'
        case Type.FINAL_DIVIDEND:
            return 'D'
        case Type.SPECIAL_DIVIDEND:
            return 'D'
    }
    return 'C'
}

class CorporateActionTool extends DrawingBase<CorporateActionOptions, CorporateActionDrawingSettings, CorporateActionState> {

    initialPoint = 0
    private findHoveredData(x: number, y: number): CorporateAction | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio);
        const sy = scaledCoordinate(y, this.verticalPixelRatio);
        const data = this._state.data;
        if (!data || typeof data !== 'object') {
            console.warn("Data", data);
            return null;
        }
        const circleRadius = 10 * window.devicePixelRatio;
        const marginBottom = 5 * window.devicePixelRatio;
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom;
        const primaryData = this._chart.getRenderedPrimaryData();

        if (!primaryData) {
            return null;
        }
        for (const timestamp in data) {
            const groupedActions = data[timestamp];
            if (Array.isArray(groupedActions)) {
                for (let i = 0; i < groupedActions.length; i++) {
                    const action = groupedActions[i]
                    const timeInSeconds = Number(timestamp);
                    const index = getTimeIndex(primaryData, timeInSeconds);
                    if (index === undefined || index < 0 || isNaN(index)) {
                        continue;
                    }

                    const xCoordinate = scaledCoordinate(
                        this._chartApi.timeScale().timeToCoordinate(primaryData[index].time) as number,
                        this.horizontalPixelRatio
                    );

                    if (!xCoordinate) {
                        continue;
                    }

                    const yCoordinate = canvasHeight - i * (circleRadius + 30);
                    const dx = sx - xCoordinate;
                    const dy = sy - yCoordinate;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance <= circleRadius) {
                        this.initialPoint = index;
                        return action;
                    }
                }
            }
        }

        return null;
    }
    private clickedCircles: string | null;

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        this.clickedCircles = null
        this._chartApi.subscribeClick(this.handleChartClick.bind(this))
        this._chartApi.timeScale().subscribeVisibleTimeRangeChange(() => {
            this._chart.publishEvent(ChartEvents.CORPORATE_CLICKED, null)
            this.clickedCircles = null
        })
    }

    private handleChartClick(param: MouseEventParams): void {
        const { point } = param
        if (!point) return
        const clickData = this.findHoveredData(point.x, point.y)
        if (clickData) {
            const actionKey = `${clickData.recordDate}-${clickData.type}-${clickData.exDate}`;
            if (this.clickedCircles === actionKey) {
                this.clickedCircles = null;
                this._chart.publishEvent(ChartEvents.CORPORATE_CLICKED, null);
            } else {
                this.clickedCircles = actionKey;
                this._chart.publishEvent(ChartEvents.CORPORATE_CLICKED, {
                    x: point.x,
                    y: point.y - 10,
                    dataType: clickData.type,
                    type: this.getDrawingType(),
                    exDate: clickData.exDate,
                    recordDate: clickData.recordDate,
                    announcementDate: clickData.announcementDate,
                    dividendAmount: clickData.dividendAmount,
                    oldFaceValue: clickData.oldFaceValue,
                    newFaceValue: clickData.newFaceValue,
                    ratioNumerator: clickData.ratioNumerator,
                    ratioDenominator: clickData.ratioDenominator,
                    rightsPremium: clickData.rightsPremium,
                });
            }
        } else {
            this._chart.publishEvent(ChartEvents.CORPORATE_CLICKED, null)
            this.clickedCircles = null
        }
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context;
        const { data } = this._state;
        if (!data) return;

        const visibleLogicalRange = this._chartApi.timeScale().getVisibleLogicalRange();
        if (!visibleLogicalRange) return;

        const barsInfo = this.getSeries().barsInLogicalRange(visibleLogicalRange);
        if (!barsInfo) return;
        const devicePixelRatio = window.devicePixelRatio || 1
        const baseSize = 10;
        const circleRadius = baseSize * devicePixelRatio;
        const marginBottom = 5 * devicePixelRatio;
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom;
        const adjustedFontSize = baseSize * devicePixelRatio
        const primaryData = this._chart.getRenderedPrimaryData();
        if (!primaryData) return


        Object.keys(data).forEach((key) => {
            const actions = data[Number(key)];
            if (Array.isArray(actions) && actions.length === 0) return;
            const timeInSeconds = Number(key);
            const index = getTimeIndex(primaryData, timeInSeconds);
            if (index === undefined || index < 0 || isNaN(index)) return
            const xCoordinate = scaledCoordinate(
                this._chartApi.timeScale().timeToCoordinate(primaryData[index].time) as number,
                this.horizontalPixelRatio
            );

            if (!xCoordinate) return;

            if (Array.isArray(actions))
                actions.forEach((action: CorporateAction, i: any) => {
                    const actionKey = `${action.recordDate}-${action.type}-${action.exDate}`;
                    const yCoordinate = canvasHeight - i * (circleRadius + 30);
                    ctx.save();
                    ctx.translate(xCoordinate, yCoordinate);

                    ctx.beginPath()
                    ctx.arc(0, 0, circleRadius + 2, 0, Math.PI * 2)
                    ctx.strokeStyle = this._chart.theme.palette.background.paper
                    ctx.lineWidth = 2
                    ctx.stroke()
                    ctx.closePath()

                    ctx.beginPath();
                    ctx.arc(0, 0, circleRadius, 0, Math.PI * 2);
                    ctx.strokeStyle = getColorForAction(action.type);
                    ctx.lineWidth = 3;
                    ctx.stroke();
                    ctx.closePath();
                    ctx.beginPath();
                    ctx.arc(0, 0, circleRadius - 2, 0, Math.PI * 2);
                    ctx.fillStyle = this.clickedCircles === actionKey
                        ? getColorForAction(action.type)
                        : this._chart.theme.palette.background.paper;

                    ctx.fill();
                    ctx.closePath();
                    ctx.fillStyle = this.clickedCircles === actionKey
                        ? 'white'
                        : getColorForAction(action.type);
                    ctx.font = `bold ${adjustedFontSize}px Arial`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(getCorporateActionLabel(action.type), 0, 0);
                    ctx.restore();
                })
        });
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const hoveredData = this.findHoveredData(x, y);
        if (hoveredData) {
            this._hovered = true;
            // const primaryData = this._chart.getRenderedPrimaryData();
            // const targetCandle = primaryData[this.initialPoint];
            // const timestamp = (targetCandle.time as number) * 1000;
            // TODO: Chart optimization
            // this._chart.setCrossHairPosition(targetCandle.customValues, timestamp);
            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: `${getColorForAction(hoveredData.type)}`,
                        visible: true,
                    },
                    horzLine: {
                        visible: false,
                    },
                },
            });

            return {
                externalId: this.id,
                zOrder: 'top',
                cursorStyle: 'pointer',
            };
        } else if (this._hovered) {
            this._hovered = false;

            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: '#758696',
                        visible: true,
                    },
                    horzLine: {
                        visible: true,
                    },
                },
            });
        }

        return null;
    }

    updateHandle = (id: string, time: Time, price: number) => {

    }
    
    onSettingsUpdate = (settings: Partial<CorporateActionDrawingSettings>) => {
        if (settings.data) {
            this._state.data = settings.data
        }
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.primitive?.requestUpdate()
    }


    getAvailableSettings = (): DrawingSettingGroup<CorporateActionDrawingSettings, SettingValues>[] => {
        return []
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'corporateAction':
                return 'corporateAction'
        }
    }

    getSettingsValues(): CorporateActionDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        return []
    }
}

export default CorporateActionTool